// MobileMovies — aba FILMES da superfície MOBILE (touch). Parede A5.
//
// Chips de categoria (horizontais, tapáveis) + grade vertical de VOD. Reusa a
// MESMA camada de dados da MoviesScreen da TV: api.getVodCategories (chips),
// api.getRecentVod ("Todos") e api.getVod(catId) (categoria). Keys scopadas via
// useProviderQueryKey — mesmo cache que a TV. SÓ a apresentação é touch.
//
// no_provider: mostra o mesmo CTA "configure fonte" da Home (MobileNoSource),
// não crash. Sem virtualização: o catálogo mobile usa LIMITE generoso por
// categoria (suficiente pra navegar; busca dedicada vem na A6). Imagens lazy
// (Poster loading="lazy" por padrão).

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { api, ApiError } from '../api'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'

import { buildCatalogChips } from './mobile-catalog'
import styles from './MobileCatalog.module.css'
import { MobileChips } from './MobileChips'
import { MobileGrid } from './MobileGrid'
import type { MobileHomeItem } from './MobileHome'
import { MobileEmpty, MobileLoading, MobileNoSource } from './MobileStates'
import { MobileVodCard } from './MobileVodCard'

/** Limite por categoria — generoso pra navegar sem paginação manual; o catálogo
 *  pesado é tarefa da busca (A6). */
const PAGE_SIZE = 120

function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export interface MobileMoviesProps {
  /** Tap num card → abre a tela de DETALHE (não direto o player). */
  onOpenItem: (item: MobileHomeItem) => void
  onConfigureSource?: (() => void) | undefined
}

export function MobileMovies({ onOpenItem, onConfigureSource }: MobileMoviesProps): JSX.Element {
  const { t } = useTranslation()
  // null = chip "Todos" (recentes); senão id da categoria.
  const [activeCat, setActiveCat] = useState<string | null>(null)

  const categoriesQ = useQuery({
    queryKey: useProviderQueryKey(['vod-categories']),
    queryFn: () => api.getVodCategories(),
    staleTime: 30 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  // "Todos" usa os recentes (rápido, mesma query da Home); categoria usa getVod.
  const listQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-vod-list', activeCat ?? 'all', PAGE_SIZE]),
    queryFn: () =>
      activeCat ? api.getVod(activeCat, 1, PAGE_SIZE) : api.getRecentVod(PAGE_SIZE),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const chips = useMemo(
    () => buildCatalogChips(categoriesQ.data ?? [], t('common.all')),
    [categoriesQ.data, t],
  )
  const items = useMemo(() => listQ.data ?? [], [listQ.data])

  const noProvider = isNoProvider(categoriesQ.error) || isNoProvider(listQ.error)
  if (noProvider) {
    return <MobileNoSource onConfigureSource={onConfigureSource} />
  }

  return (
    <div className={styles.screen} data-screen="movies">
      <MobileChips
        chips={chips}
        activeId={activeCat}
        onSelect={setActiveCat}
        ariaLabel={t('nav.movies')}
      />

      {listQ.isLoading && items.length === 0 ? (
        <MobileLoading />
      ) : items.length === 0 ? (
        <MobileEmpty title={t('empty.noCategoryTitle')} text={t('empty.noCategoryDesc')} />
      ) : (
        <MobileGrid gridId="movies">
          {items.map((m) => (
            <MobileVodCard
              key={m.id}
              title={m.name}
              poster={m.poster}
              year={m.year}
              kind="vod"
              onPress={() => onOpenItem({ id: m.id, kind: 'vod', title: m.name, poster: m.poster })}
            />
          ))}
        </MobileGrid>
      )}
    </div>
  )
}
